import { motion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";

import { cn } from "@/utils";
import styles from "@/ui/styles/shared.module.css";

import { userDetails } from "@/constants";
import { fadeIn } from "@/utils/motion";
import { Section } from "@/ui/organism/section";

const contactChannels = [
	{
		label: "Email",
		value: userDetails.email,
		href: userDetails.email ? `mailto:${userDetails.email}` : undefined,
	},
	{
		label: "GitHub",
		value: userDetails.github,
		href: userDetails.github,
	},
	{
		label: "LinkedIn",
		value: userDetails.linkedin,
		href: userDetails.linkedin,
	},
];

const defaultBrief = {
	callSign: "",
	objective: "",
	timeline: "ASAP",
};

const Contact = () => {
	const [brief, setBrief] = useState(defaultBrief);

	const handleChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name, value } = event.target;
		setBrief((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!userDetails.email) return;

		const subject = encodeURIComponent(
			`Mission Brief: ${brief.callSign || "New Inquiry"}`,
		);
		const body = encodeURIComponent(
			`Objective:\n${brief.objective}\n\nTimeline:\n${brief.timeline}`,
		);
		const mailto = `mailto:${userDetails.email}?subject=${subject}&body=${body}`;

		if (typeof window !== "undefined") {
			window.location.href = mailto;
		}
	};

	return (
		<Section id="contact" label="Contact" order={5}>
			<div className={cn(
				"rounded-[24px] sm:rounded-[32px] border border-white/10 bg-black/30 px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10 shadow-[0_25px_80px_rgba(5,7,19,0.65)] backdrop-blur-xl",
				styles.commonShell
			)}>
				<motion.div variants={fadeIn("up", "spring", 0.1, 0.8)}>
					<p className="sectionSubText">Mission control</p>
					<h2 className="sectionHeadText">Open comms</h2>
					<p className="paragraphText mt-4 max-w-3xl text-white/70 text-sm sm:text-base md:text-lg">
						Tell me what you'd like to launch, who's on deck, and when you need
						it live. I reply within 1–2 business days with next steps or a quick
						loom.
					</p>
				</motion.div>

				<div className="mt-8 sm:mt-10 md:mt-12 grid gap-6 md:gap-8 lg:grid-cols-[0.85fr_1.15fr]">
					<div className="space-y-4 sm:space-y-6 rounded-[20px] sm:rounded-[28px] border border-white/10 bg-white/5 p-4 sm:p-6 backdrop-blur">
						<p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-white/50">
							Channels
						</p>
						<div className="space-y-4 sm:space-y-6">
							{contactChannels
								.filter((channel) => channel.value)
								.map((channel) => (
									<div key={channel.label}>
										<p className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/40">
											{channel.label}
										</p>
										{channel.href ? (
											<Link
												href={channel.href}
												target="_blank"
												rel="noreferrer noopener"
												className="text-base sm:text-lg font-semibold text-white hover:text-[#ffe36c] break-all"
											>
												{channel.value}
											</Link>
										) : (
											<p className="text-base sm:text-lg font-semibold text-white">
												{channel.value}
											</p>
										)}
									</div>
								))}
						</div>
						<div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 p-3 sm:p-4">
							<p className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/40">
								Availability
							</p>
							<p className="mt-2 text-base sm:text-lg font-semibold text-white">
								Booking Q1–Q2 2026
							</p>
							<p className="text-xs sm:text-sm text-white/60">
								Open to remote or hybrid collaborations.
							</p>
						</div>
					</div>

					<form
						className="space-y-4 sm:space-y-6 rounded-[20px] sm:rounded-[28px] border border-white/10 bg-gradient-to-b from-white/10 to-white/0 p-4 sm:p-6 backdrop-blur"
						onSubmit={handleSubmit}
					>
						<div>
							<label
								htmlFor="callSign"
								className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/50"
							>
								Your call sign / team
							</label>
							<input
								id="callSign"
								name="callSign"
								value={brief.callSign}
								onChange={handleChange}
								placeholder="Example: Lunar Labs · Product Design"
								className="mt-2 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white outline-none focus:border-[#ffe36c]"
							/>
						</div>
						<div>
							<label
								htmlFor="objective"
								className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/50"
							>
								Mission objective
							</label>
							<textarea
								id="objective"
								name="objective"
								value={brief.objective}
								onChange={handleChange}
								rows={5}
								placeholder="What are we building? Goals, audience, stack…"
								className="mt-2 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white outline-none focus:border-[#ffe36c] resize-none"
							/>
						</div>
						<div>
							<label
								htmlFor="timeline"
								className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-white/50"
							>
								Timeline / launch window
							</label>
							<input
								id="timeline"
								name="timeline"
								value={brief.timeline}
								onChange={handleChange}
								placeholder="ASAP, Q2, specific date…"
								className="mt-2 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base text-white outline-none focus:border-[#ffe36c]"
							/>
						</div>
						<button
							type="submit"
							className="w-full rounded-full bg-gradient-to-r from-[#ffe36c] to-[#c58bff] px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-semibold uppercase tracking-[0.3em] transition hover:brightness-110"
              style={{ color: "black" }}
						>
							Send mission brief
						</button>
					</form>
				</div>
			</div>
		</Section>
	);
};

export default Contact;
